import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { DEMARCHES, STATUS_LABELS } from "@/data/studentData";
import { Check, Circle, Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const STATUSES = ["not_started", "in_progress", "done"];

export default function Demarches() {
  const [statuses, setStatuses] = useState({});
  const [customs, setCustoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newCat, setNewCat] = useState("Personnel");
  const [adding, setAdding] = useState(false);

  const loadAll = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/demarches/my"),
        api.get("/demarches/custom"),
      ]);
      const map = {};
      s.data.forEach((x) => { map[x.demarche_id] = x.status; });
      setStatuses(map);
      setCustoms(c.data);
    } catch {
      toast.error("Impossible de charger tes démarches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const toggle = async (demarcheId) => {
    const current = statuses[demarcheId] || "not_started";
    const next = STATUSES[(STATUSES.indexOf(current) + 1) % STATUSES.length];
    setStatuses((p) => ({ ...p, [demarcheId]: next }));
    try {
      await api.post("/demarches/toggle", { demarche_id: demarcheId, status: next });
    } catch {
      toast.error("Erreur de sauvegarde");
      setStatuses((p) => ({ ...p, [demarcheId]: current }));
    }
  };

  const addCustom = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/demarches/custom", {
        title: newTitle.trim(),
        description: newDesc.trim(),
        category: newCat.trim() || "Personnel",
      });
      setCustoms((c) => [data, ...c]);
      setOpen(false);
      setNewTitle(""); setNewDesc(""); setNewCat("Personnel");
      toast.success("Démarche ajoutée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ajout impossible");
    } finally {
      setAdding(false);
    }
  };

  const removeCustom = async (id) => {
    if (!window.confirm("Supprimer cette démarche personnelle ?")) return;
    try {
      await api.delete(`/demarches/custom/${id}`);
      setCustoms((c) => c.filter((x) => x.id !== id));
      setStatuses((p) => { const n = { ...p }; delete n[id]; return n; });
      toast.success("Supprimée");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const allItems = useMemo(() => [
    ...DEMARCHES,
    ...customs.map((c) => ({ id: c.id, title: c.title, description: c.description, category: c.category, isCustom: true })),
  ], [customs]);

  const { doneCount, totalCount, pct } = useMemo(() => {
    const total = allItems.length;
    const done = allItems.filter((d) => statuses[d.id] === "done").length;
    return { doneCount: done, totalCount: total, pct: total === 0 ? 0 : Math.round((done / total) * 100) };
  }, [statuses, allItems]);

  const grouped = useMemo(() => {
    const cats = {};
    allItems.forEach((d) => {
      const k = d.category || "Autre";
      if (!cats[k]) cats[k] = [];
      cats[k].push(d);
    });
    return cats;
  }, [allItems]);

  return (
    <div className="space-y-8" data-testid="demarches-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Évolution</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Mes démarches</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Coche ce que tu as commencé ou terminé. Clique pour basculer : <em>à faire → en cours → terminé</em>.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-500 hover:bg-indigo-600" data-testid="add-custom-btn">
              <Plus size={16} className="mr-2" /> Ajouter une démarche
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-custom-dialog">
            <DialogHeader>
              <DialogTitle>Nouvelle démarche personnelle</DialogTitle>
              <DialogDescription>Ajoute une démarche qui te concerne, hors liste pré-remplie.</DialogDescription>
            </DialogHeader>
            <form onSubmit={addCustom} className="space-y-4">
              <div>
                <Label htmlFor="t">Titre</Label>
                <Input id="t" required value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                       placeholder="Ex : Inscrire mon vélo en marquage Bicycode"
                       className="mt-1.5" data-testid="custom-title-input" />
              </div>
              <div>
                <Label htmlFor="d">Description (optionnel)</Label>
                <Input id="d" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                       className="mt-1.5" data-testid="custom-desc-input" />
              </div>
              <div>
                <Label htmlFor="c">Catégorie</Label>
                <Input id="c" value={newCat} onChange={(e) => setNewCat(e.target.value)}
                       placeholder="Personnel" className="mt-1.5" data-testid="custom-cat-input" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={adding} className="bg-indigo-500 hover:bg-indigo-600" data-testid="custom-save-btn">
                  {adding ? "Ajout..." : "Ajouter"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {/* Progress */}
      <div className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md py-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-5">
          <div className="flex-1">
            <div className="flex justify-between items-center mb-2">
              <p className="font-display font-semibold text-slate-900">Progression globale</p>
              <span className="font-display text-2xl font-bold text-indigo-600" data-testid="progress-value">{pct}%</span>
            </div>
            <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-sm text-slate-500 mt-2">{doneCount} / {totalCount} démarches terminées</p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Chargement...
        </div>
      )}

      {!loading && Object.entries(grouped).map(([cat, items]) => (
        <section key={cat}>
          <h2 className="font-display text-xl font-semibold text-slate-800 mb-3">{cat}</h2>
          <div className="space-y-2">
            {items.map((d) => {
              const st = statuses[d.id] || "not_started";
              return (
                <div
                  key={d.id}
                  data-testid={`demarche-${d.id}`}
                  className={`group w-full flex items-start gap-4 p-4 rounded-xl border text-left transition-all ${
                    st === "done"
                      ? "bg-slate-50 border-slate-100"
                      : st === "in_progress"
                      ? "bg-white border-indigo-200 shadow-[0_2px_8px_-2px_rgba(99,102,241,0.15)]"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <button
                    onClick={() => toggle(d.id)}
                    aria-label="Basculer le statut"
                    data-testid={`toggle-${d.id}`}
                    className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border-2 shrink-0 transition-colors ${
                      st === "done"
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : st === "in_progress"
                        ? "border-indigo-500 text-indigo-500"
                        : "border-slate-300"
                    }`}
                  >
                    {st === "done" ? <Check size={14} /> : st === "in_progress" ? <Loader2 size={12} className="animate-spin" /> : <Circle size={12} className="opacity-0" />}
                  </button>
                  <button
                    onClick={() => toggle(d.id)}
                    className="flex-1 text-left"
                  >
                    <p className={`font-medium ${st === "done" ? "line-through text-slate-400" : "text-slate-900"}`}>
                      {d.title}
                      {d.isCustom && <span className="ml-2 text-[10px] uppercase tracking-wider text-indigo-500 font-semibold">perso</span>}
                    </p>
                    {d.description && (
                      <p className={`text-sm mt-0.5 ${st === "done" ? "text-slate-400" : "text-slate-500"}`}>{d.description}</p>
                    )}
                  </button>
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full shrink-0 ${
                      st === "done"
                        ? "bg-emerald-100 text-emerald-700"
                        : st === "in_progress"
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {STATUS_LABELS[st]}
                  </span>
                  {d.isCustom && (
                    <button
                      onClick={() => removeCustom(d.id)}
                      data-testid={`remove-${d.id}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 ml-1"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
